import type { FormSubmission, ApiResponse } from '../types/form.types'
import { config } from '../config/config'
import type { GeoJSONFeatureCollection, GeoJSONGeometry } from '../types/geojson.types'
import type { UserDetails } from '@/types/user.types'
import { fetchAuthSession } from 'aws-amplify/auth'

interface RekognitionErrorInfo {
  errorType: string;
  userMessage: string;
  shouldRetry: boolean;
  timestamp: string;
}

export interface UploadResult {
  s3Url: string;
  s3Path: string;
  bucketType?: 'approved' | 'pending';
  fileInfo?: {
    originalName: string;
    size: number;
    type: string;
  };
  rekognitionResult?: {
    isValid: boolean;
    confidence: number;
    labels?: string[];
    moderationLabels?: string[];
    errorInfo?: RekognitionErrorInfo;
  };
}

export interface ApiUploadError extends Error {
  status?: number;
  details?: {
    message?: string;
    details?: string[];
  };
}

export interface GeoSearchResult {
  place_id: number | string
  display_name: string
  name?: string
  class?: string
  type?: string
  address?: Record<string, string>
  geojson?: GeoJSONGeometry
  lat?: string
  lon?: string
  osm_id?: number | string
  osm_type?: string
  [key: string]: unknown
}

export type LocationSearchResponse = GeoSearchResult[] | GeoJSONFeatureCollection

export class ApiService {
  private static baseUrl = config.apiUrl

  static async getAuthHeader(): Promise<Record<string, string>> {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      const accessToken = session.tokens?.accessToken?.toString();
      const token = idToken ?? accessToken;
      if (!token) {
        console.warn('No auth token available for API request');
        return {};
      }
      const tokenParts = token.split('.');
      console.log('Auth token found for API request', {
        type: idToken ? 'id' : 'access',
        isJwt: tokenParts.length === 3,
        tokenLength: token.length,
      });
      return { Authorization: `Bearer ${token}` };
    } catch (e) {
      console.warn('Failed to get auth token', e);
      return {};
    }
  }

  static async submitForm(data: FormSubmission): Promise<ApiResponse> {
    try {
      console.log('Starting form submission...')
      const authHeader = await this.getAuthHeader();
      console.log('Submitting to API', {
        baseUrl: this.baseUrl,
        hasAuthHeader: Boolean(authHeader.Authorization),
        authIsBearer: authHeader.Authorization?.startsWith('Bearer ') ?? false,
      })

      const response = await fetch(`${this.baseUrl}/forms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        },
        body: JSON.stringify(data),
      })

      console.log('Raw response:', response)
      console.log('Full response details:', {
        status: response.status,
        statusText: response.statusText,
        headers: {
          'content-type': response.headers.get('content-type'),
          'content-length': response.headers.get('content-length'),
          'x-amzn-errortype': response.headers.get('x-amzn-errortype'),
          'x-amzn-requestid': response.headers.get('x-amzn-requestid'),
          'x-amz-apigw-id': response.headers.get('x-amz-apigw-id'),
          'www-authenticate': response.headers.get('www-authenticate'),
        },
      })

      if (!response.ok) {
        const errorText = await response.text()
        let errorMessage = `HTTP error! status: ${response.status}`
        try {
          const parsed = JSON.parse(errorText)
          if (parsed.message) errorMessage = parsed.message
        } catch {}
        throw new Error(errorMessage)
      }

      const responseText = await response.text() // Get raw response text first
      console.log('Raw response text:', responseText)

      let jsonResponse: ApiResponse
      try {
        jsonResponse = JSON.parse(responseText)
        console.log('Parsed response:', JSON.stringify(jsonResponse, null, 2))
      } catch (parseError) {
        console.error('Error parsing response:', parseError)
        throw new Error('Invalid JSON response from server')
      }

      return jsonResponse
    } catch (error) {
      console.error('API Error:', error)
      throw error
    }
  }

  static async registerUser(data: UserDetails): Promise<ApiResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Registration Error:', error)
      throw error
    }
  }

  static async uploadFile(
    file: File,
    fileType: 'logo' | 'glb' | 'map-marker' | 'ar-asset' | 'background' | 'mind',
    unifiedFileName: string,
  ): Promise<UploadResult> { // Return the full response object
    try {
      console.log('Starting file upload...', {
        fileName: file.name,
        unifiedFileName,
        fileSize: file.size,
        fileType
      })

      const formData = new FormData()
      formData.append('file', file)
      // The backend already routes all image assets through the logo/Rekognition path.
      // 'mind' maps 1:1 — compiled MindAR targets skip Rekognition and go straight to approved.
      const backendFileType = fileType === 'mind'
        ? 'mind'
        : (fileType === 'ar-asset' || fileType === 'glb') ? 'glb' : 'logo'
      formData.append('fileType', backendFileType)
      formData.append('unifiedFileName', unifiedFileName)

      const authHeader = await this.getAuthHeader();
      console.log('Uploading to API', {
        baseUrl: this.baseUrl,
        hasAuthHeader: Boolean(authHeader.Authorization),
        authIsBearer: authHeader.Authorization?.startsWith('Bearer ') ?? false,
      })
      const response = await fetch(`${this.baseUrl}/upload`, {
        method: 'POST',
        headers: {
            ...authHeader
        },
        body: formData,
      })

      console.log('Upload response:', response)

      if (!response.ok) {
        // Try to get the error details from the response
        let errorDetails: { message?: string; details?: string[] } = { message: 'Unknown error' };
        try {
          const errorBody = await response.text();
          console.error('🚨 Upload Error Response Body:', errorBody);
          try {
            // Try to parse as JSON
            errorDetails = JSON.parse(errorBody);
          } catch {
            // If not JSON, use as string message
            errorDetails = { message: errorBody };
          }
        } catch (parseError) {
          console.error('Could not parse error response:', parseError);
        }
        // Create a structured error that includes the parsed details
        const error = new Error(`HTTP error! status: ${response.status}, details: ${JSON.stringify(errorDetails)}`) as ApiUploadError;
        error.status = response.status;
        error.details = errorDetails;
        throw error;
      }

      const result = await response.json() as UploadResult
      console.log('Upload result:', result)

      if (!result.s3Url || !result.s3Path) {
        throw new Error('Upload response did not include s3Url or s3Path');
      }

      // Sanity-check the server-side file size against what we sent. If the
      // server reports a dramatically smaller file we've hit the Lambda proxy
      // payload ceiling (6MB) and the multipart parser has produced a stub —
      // refuse to proceed with a silently-broken asset.
      const serverSize = result.fileInfo?.size
      if (typeof serverSize === 'number' && serverSize > 0 && file.size > 0) {
        const deltaRatio = Math.abs(file.size - serverSize) / file.size
        if (serverSize < 1024 || deltaRatio > 0.2) {
          console.error('🚨 Upload size mismatch', {
            sentBytes: file.size,
            serverBytes: serverSize,
            deltaRatio,
          })
          throw new Error(
            `Upload appears corrupted (sent ${file.size} bytes, server stored ${serverSize}). ` +
            `This usually means the file exceeded our upload limit. Please try a smaller or compressed file.`
          )
        }
      }

      return result // Return the full result
    } catch (error) {
      console.error('Upload Error:', error)
      throw error
    }
  }

  static async validateImageWithRekognition(s3Path: string, bucketType: string): Promise<{
    isValid: boolean
    confidence: number
    labels?: string[]
    moderationLabels?: string[]
    errorInfo?: {
      errorType: string
      userMessage: string
      shouldRetry: boolean
      timestamp: string
    }
  }> {
    try {
      console.log('Starting image validation with AWS Rekognition...', { s3Path, bucketType })
      const authHeader = await this.getAuthHeader();

      const response = await fetch(`${this.baseUrl}/validate-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        },
        body: JSON.stringify({ s3Path, bucketType }),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      console.log('Rekognition validation result:', result)

      // Enhanced error information logging
      if (result.errorInfo) {
        console.warn('🚨 Rekognition reported an issue:', {
          errorType: result.errorInfo.errorType,
          userMessage: result.errorInfo.userMessage,
          shouldRetry: result.errorInfo.shouldRetry,
          s3Path: s3Path,
          timestamp: result.errorInfo.timestamp
        })
      }

      return result
    } catch (error) {
      console.error('Image validation error:', error)

      // Enhanced error logging for API failures
      console.error('🚨 API validation failure:', {
        endpoint: `${this.baseUrl}/validate-image`,
        s3Path: s3Path,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      })

      throw error
    }
  }

  static async getUploadProgress(uploadId: string): Promise<{
    progress: number
    status: 'uploading' | 'processing' | 'completed' | 'error'
    error?: string
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/upload-progress/${uploadId}`)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Upload progress error:', error)
      throw error
    }
  }

  // === STEP FUNCTIONS METHODS ===
  static async startGLBApprovalWorkflow(assetData: {
    assetKey: string
    bucket: string
    fileName: string
    fileSize: number
    uploadTimestamp: string
  }): Promise<{
    executionArn: string
    startDate: string
  }> {
    try {
      const authHeader = await this.getAuthHeader();
      const response = await fetch(`${this.baseUrl}/start-glb-approval`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        },
        body: JSON.stringify(assetData)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Start GLB approval workflow error:', error)
      throw error
    }
  }

  static async pollStepFunctionStatus(executionArn: string): Promise<{
    status: 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'TIMED_OUT' | 'ABORTED'
    output?: unknown
    error?: string
  }> {
    try {
      // Note: This would typically require AWS SDK client-side or a backend polling endpoint
      // For now, we'll implement a simple approach that checks if the asset moved to approved bucket
      // In a production system, you'd want a dedicated polling endpoint

      const response = await fetch(`${this.baseUrl}/step-function-status/${encodeURIComponent(executionArn)}`)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Step Function status polling error:', error)
      throw error
    }
  }

  static async getCampaignStateMachineStatus(submissionId: string): Promise<{
    status: 'NOT_FOUND' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'TIMED_OUT' | 'ABORTED'
    campaignId?: string
    executionArn?: string
    startDate?: string
    stopDate?: string
    states: Array<{
      name: string
      status: 'pending' | 'running' | 'completed' | 'failed'
      timestamp?: string
      errorMessage?: string
      errorType?: string
    }>
    message?: string
    error?: string
    errorMessage?: string
    failedState?: string
    failureCause?: string
  }> {
    try {
      console.log(`Polling state machine status for submissionId: ${submissionId}`)

      const authHeader = await this.getAuthHeader();
      const response = await fetch(`${this.baseUrl}/state-machine-status/${encodeURIComponent(submissionId)}`, {
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      console.log('State machine status result:', result)

      return result
    } catch (error) {
      console.error('Campaign state machine status polling error:', error)
      throw error
    }
  }

  static async searchLocation(
    query: string,
    options: { polygonOnly?: boolean } = {}
  ): Promise<LocationSearchResponse> {
    try {
      if (!query || query.length < 3) return [];
      const searchBaseUrl = config.geoApiUrl.replace(/\/$/, '');
      const authHeader = await this.getAuthHeader();
      const params = new URLSearchParams({
        q: query,
        format: 'jsonv2',
        polygon_geojson: '1',
        limit: '5'
      });

      if (options.polygonOnly) {
        params.set('polygonOnly', '1');
      }

      const response = await fetch(`${searchBaseUrl}/location/search?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = (await response.json()) as LocationSearchResponse
      return result
    } catch (error) {
      console.error('Location search error:', error)
      throw error
    }
  }
}
